import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { CommandDock } from './CommandDock'
import { ContextFlyout } from './ContextFlyout'
import { DetailPanel } from './DetailPanel'
import { GlobalHeader } from './GlobalHeader'
import { IconFilter } from './icons'
import { MobilePrimaryNav } from './MobilePrimaryNav'
import { OfflineBanner } from './OfflineBanner'
import { isMobileNavWidth } from './navigation'
import {
  ShellLayoutProvider,
  useShellLayout,
  type ShellUtilityItem,
} from './ShellLayoutContext'
import { UtilityRail } from './UtilityRail'
import { Workspace } from './Workspace'

function AppShellFrame() {
  const [mobile, setMobile] = useState(() =>
    typeof window === 'undefined' ? false : isMobileNavWidth(window.innerWidth),
  )
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const {
    contextPanel,
    detailPanel,
    workspaceMode,
    contextPanelTitle,
    detailPanelTitle,
    contextRail,
    intelligenceRail,
    utilityRail,
    utilityItems,
    contextFlyoutOpen,
    setContextFlyoutOpen,
    toggleContextFlyout,
    detailCollapsed,
    detailOpenMobile,
    setDetailCollapsed,
    setDetailOpenMobile,
    closeShellOverlays,
  } = useShellLayout()
  const location = useLocation()

  useEffect(() => {
    const onResize = () => setMobile(isMobileNavWidth(window.innerWidth))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Expanded Context must not leak across unrelated routes.
  useEffect(() => {
    closeShellOverlays()
    setMobileNavOpen(false)
  }, [location.pathname, closeShellOverlays])

  const resolvedUtilityItems = useMemo<ShellUtilityItem[]>(() => {
    if (utilityItems.length > 0) return utilityItems
    if (contextPanel != null && contextRail !== 'hidden') {
      return [
        {
          id: 'context-filters',
          label: contextPanelTitle || 'Filters',
          icon: <IconFilter />,
          opensFlyout: true,
        },
      ]
    }
    return []
  }, [utilityItems, contextPanel, contextRail, contextPanelTitle])

  const showUtility = utilityRail !== 'hidden'
  const showDetail = intelligenceRail !== 'hidden' && detailPanel != null
  const flyoutAvailable = contextPanel != null && contextRail !== 'hidden'

  function handleUtilitySelect(item: ShellUtilityItem) {
    if (item.opensFlyout) {
      if (mobileNavOpen) setMobileNavOpen(false)
      toggleContextFlyout()
      item.onSelect?.()
      return
    }
    item.onSelect?.()
  }

  function handleMobileNavOpenChange(open: boolean) {
    if (open) {
      closeShellOverlays()
    }
    setMobileNavOpen(open)
  }

  return (
    <div
      className="app-shell"
      data-nav-mode={mobile ? 'mobile' : 'desktop'}
      data-context-rail={contextRail}
      data-intelligence-rail={intelligenceRail}
      data-utility-rail={utilityRail}
      data-has-context={flyoutAvailable ? 'true' : 'false'}
      data-has-detail={showDetail ? 'true' : 'false'}
      data-context-flyout={contextFlyoutOpen ? 'open' : 'closed'}
      data-testid="app-shell"
    >
      <OfflineBanner />
      <GlobalHeader
        mobile={mobile}
        onOpenMobileNav={() => handleMobileNavOpenChange(true)}
      />

      <div className="shell-body" data-testid="shell-body">
        {showUtility && (
          <UtilityRail
            items={resolvedUtilityItems}
            flyoutOpen={contextFlyoutOpen}
            onSelectItem={handleUtilitySelect}
          />
        )}

        <div className="shell-main" data-testid="shell-main">
          <Workspace mode={workspaceMode}>
            <Outlet />
          </Workspace>

          {flyoutAvailable && (
            <ContextFlyout
              open={contextFlyoutOpen}
              label={contextPanelTitle}
              onClose={() => setContextFlyoutOpen(false)}
            >
              {contextPanel}
            </ContextFlyout>
          )}
        </div>

        {showDetail && (
          <DetailPanel
            collapsed={!mobile && detailCollapsed}
            onToggleCollapse={mobile ? undefined : () => setDetailCollapsed(!detailCollapsed)}
            mobileOpen={mobile && detailOpenMobile}
            onCloseMobile={() => setDetailOpenMobile(false)}
            title={detailPanelTitle}
          >
            {detailPanel}
          </DetailPanel>
        )}
      </div>

      <CommandDock mobile={mobile} />
      {mobile && (
        <MobilePrimaryNav
          open={mobileNavOpen}
          onClose={() => handleMobileNavOpenChange(false)}
        />
      )}
    </div>
  )
}

export function AppShell() {
  return (
    <ShellLayoutProvider>
      <AppShellFrame />
    </ShellLayoutProvider>
  )
}
